import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { StatusIndicatorIcon } from '../types';
import { StatusIcon } from './status-icon';

type Props = {
  isFailedCall?: boolean;
  isPoorAudio?: boolean;
  isSkippedCall?: boolean;
  type: 'analysis' | 'summary' | 'task' | 'editHistory';
  styles?: Interpolation<Theme>;
};

type ContentProps = {
  message: string;
  statusIcon: StatusIndicatorIcon;
  styles?: Interpolation<Theme>;
};

const RenderContent = ({ message, statusIcon, styles, ...rest }: ContentProps) => (
  <div css={[contentStyles, styles]} {...rest}>
    <StatusIcon statusIcon={statusIcon} />
    <Text color='subdued' size='small'>
      {message}
    </Text>
  </div>
);

export const CallAnalyzeStatus = ({ isFailedCall, isPoorAudio, isSkippedCall, type, ...rest }: Props) => {
  const { t } = useTranslation('analytics');
  const isSummaryData = type === 'summary';
  const isTaskType = type === 'task';
  const isEditHistory = type === 'editHistory';

  if (isFailedCall) {
    return (
      <RenderContent
        message={
          isTaskType
            ? t('We are unable to analyze this call for Tasks.')
            : isSummaryData
            ? t('We are unable to transcribe and analyze this call.')
            : t('We are unable to analyze this call.')
        }
        statusIcon='error'
        {...rest}
      />
    );
  }

  if (isSkippedCall && isPoorAudio) {
    return (
      <RenderContent
        message={
          isTaskType
            ? t('Tasks may be inaccurate due to poor audio quality. Please review the recording.')
            : isSummaryData
            ? t(
                'Calls are not analyzed if they have a single participant, very short audio, or audio exceeding an hour in length.'
              )
            : t("We didn't analyze this call because the short audio length resulted in inaccurate outputs.")
        }
        statusIcon={isSummaryData ? 'help' : 'undetermined'}
        {...rest}
      />
    );
  }

  if (isSkippedCall) {
    return (
      <RenderContent
        message={t(
          'Calls are not analyzed if they have a single participant, very short audio, or audio exceeding an hour in length.'
        )}
        statusIcon='help'
        {...rest}
      />
    );
  }

  if (isPoorAudio) {
    return (
      <RenderContent
        message={
          isEditHistory
            ? t('The original analysis may be inaccurate due to poor audio quality. ')
            : isTaskType
            ? t('Tasks may be inaccurate due to poor audio quality. Please review the recording.')
            : isSummaryData
            ? t(
                'Network Performance issues may affect accuracy of Summary, Call Analysis, Transcript, and AI Tasks. Review the recording.'
              )
            : t('This analysis may be inaccurate due to poor audio quality. Please review the recording.')
        }
        statusIcon='warning'
        {...rest}
      />
    );
  }

  return null;
};

const contentStyles = css`
  align-items: center;
  display: flex;
  gap: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(4)};
`;
